/**
 * TOTP Input Component
 * 
 * This component provides a 6-digit TOTP code input interface
 * with automatic focus, validation, and submission.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Fade,
  Slide,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useAuth } from './AuthProvider';

interface TOTPInputProps {
  email: string;
  onSuccess: () => void;
  onResend: () => void | Promise<void>;
  onCancel: () => void;
  className?: string;
  autoFocus?: boolean;
}

const TOTPInput: React.FC<TOTPInputProps> = ({ 
  email, 
  onSuccess, 
  onResend, 
  onCancel, 
  className = '',
  autoFocus = true 
}) => {
  const { verifyTotp, isLoading, error, clearError } = useAuth();
  
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const [attempts, setAttempts] = useState<number>(0);
  const [isResending, setIsResending] = useState<boolean>(false);
  const [totpError, setTotpError] = useState<boolean>(false);
  const [totpErrorMessage, setTotpErrorMessage] = useState<string>('');
  
  const inputRefs = useRef<(HTMLDivElement | null)[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Sync with global auth error (e.g., backend response)
  useEffect(() => {
    if (error) {
      console.log('TOTPInput - Received auth error from context:', error);
      setTotpError(true);
      setTotpErrorMessage(typeof error === 'string' ? error : 'Invalid or expired TOTP code');
      clearError();
    }
  }, [error, clearError]);

  // Debug: Track totpError state changes
  useEffect(() => {
    console.log('TOTPInput - totpError state changed:', totpError);
  }, [totpError]);

  // Auto-focus first input
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      const firstInput = inputRefs.current[0].querySelector('input');
      firstInput?.focus();
    }
  }, [autoFocus]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      timeoutRef.current = setTimeout(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resendCooldown]);

  // Handle input change
  const handleInputChange = (index: number, value: string) => {
    // Clear TOTP error when user starts typing
    if (totpError) {
      setTotpError(false);
      setTotpErrorMessage('');
    }
    
    // Only allow single digit
    if (value.length > 1) {
      value = value.slice(-1);
    }
    
    // Only allow digits
    if (!/^\d*$/.test(value)) {
      return;
    }
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = inputRefs.current[index + 1]?.querySelector('input');
      nextInput?.focus();
    }
    
    // Auto-submit when all digits are entered
    if (newCode.every(digit => digit !== '') && !isVerifying) {
      handleSubmit(newCode.join(''));
    }
  };

  // Handle backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = inputRefs.current[index - 1]?.querySelector('input');
      prevInput?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    // Clear TOTP error when pasting
    if (totpError) {
      setTotpError(false);
      setTotpErrorMessage('');
    }
    
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      
      // Focus last input
      const lastInput = inputRefs.current[5]?.querySelector('input');
      lastInput?.focus();
      
      // Auto-submit
      if (!isVerifying) {
        handleSubmit(pastedData);
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (codeValue?: string) => {
    const codeToSubmit = codeValue || code.join('');
    
    if (codeToSubmit.length !== 6) {
      return;
    }
    
    // Check if too many attempts
    if (attempts >= 3) {
      console.log('TOTPInput - Too many attempts, redirecting to login');
      onCancel(); // Go back to login
      return;
    }
    
    setIsVerifying(true);
    setAttempts(prev => prev + 1);
    
    try {
      console.log('TOTPInput - Starting verification for:', email);
      const result = await verifyTotp(email, codeToSubmit);
      console.log('TOTPInput - Verification result:', { 
        success: result.success, 
        error: result.error,
        hasError: !!result.error 
      });
      
      if (result.success) {
        console.log('TOTPInput - Verification successful');
        setTotpError(false);
        setTotpErrorMessage('');
        onSuccess();
      } else {
        // Set TOTP error state
        console.log('TOTPInput - Verification failed, setting error state:', result.error);
        setTotpError(true);
        setTotpErrorMessage(result.error || 'Invalid or expired TOTP code');
        // Reset code on error
        setCode(['', '', '', '', '', '']);
        // Small delay to ensure error state is set before focusing
        setTimeout(() => {
          const firstInput = inputRefs.current[0]?.querySelector('input');
          firstInput?.focus();
          console.log('TOTPInput - Error state after focus:', { totpError: true });
        }, 100);
        
        // If too many attempts, show warning
        if (attempts >= 2) {
          console.log('TOTPInput - Final attempt failed, will redirect to login');
        }
      }
    } catch (err) {
      console.error('TOTPInput - Verification exception:', err);
      // Set TOTP error state
      setTotpError(true);
      setTotpErrorMessage(err instanceof Error ? err.message : 'TOTP verification failed');
      // Reset code on error
      setCode(['', '', '', '', '', '']);
      // Small delay to ensure error state is set before focusing
      setTimeout(() => {
        const firstInput = inputRefs.current[0]?.querySelector('input');
        firstInput?.focus();
        console.log('TOTPInput - Error state after exception focus:', { totpError: true });
      }, 100);
      
      // If too many attempts, show warning
      if (attempts >= 2) {
        console.log('TOTPInput - Final attempt failed, will redirect to login');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle resend
  const handleResend = async () => {
    console.log('TOTPInput - Resend button clicked', { resendCooldown, isResending });
    
    if (resendCooldown > 0 || isResending) {
      console.log('TOTPInput - Resend blocked by cooldown or already resending');
      return;
    }
    
    setIsResending(true);
    clearError(); // Clear any previous errors before resending
    
    try {
      console.log('TOTPInput - Calling onResend callback...');
      // Call the onResend callback (which will call the backend to generate a new TOTP)
      await onResend();
      console.log('TOTPInput - onResend completed successfully');
      setResendCooldown(30); // 30 second cooldown
    } catch (err) {
      console.error('TOTPInput - Resend error:', err);
    } finally {
      setIsResending(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setCode(['', '', '', '', '', '']);
    setTotpError(false);
    setTotpErrorMessage('');
    onCancel();
  };

  const isCodeComplete = code.every(digit => digit !== '');
  const canResend = resendCooldown === 0 && !isResending;
  const isDisabled = isLoading || isVerifying;

  const theme = useTheme();

  return (
    <Box className={className}>
      <Fade in timeout={600}>
        <Box>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                mb: 2,
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              <SecurityIcon sx={{ fontSize: 40, color: '#ffffff' }} />
            </Box>
            
            <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
              Two-Factor Authentication
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              We've sent a 6-digit verification code to
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: 'primary.main',
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                px: 2,
                py: 1,
                borderRadius: 2,
                display: 'inline-block'
              }}
            >
              {email}
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Slide direction="down" in={!!error} timeout={300}>
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3, 
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    width: '100%'
                  }
                }}
                onClose={clearError}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {error}
                </Typography>
                {attempts > 0 && (
                  <Typography variant="caption" color="error.main">
                    Attempt {attempts} of 3
                  </Typography>
                )}
                {attempts >= 2 && (
                  <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                    After 3 failed attempts, you'll need to log in again.
                  </Typography>
                )}
              </Alert>
            </Slide>
          )}

          {/* TOTP Form */}
          <Box
            component="form"
            onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
            sx={{ mb: 4 }}
          >
            {/* TOTP Input Container */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 4,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 2,
                  mb: totpError ? 1 : 0,
                  flexWrap: 'wrap',
                }}
              >
                {code.map((digit, index) => (
                  <TextField
                    key={index}
                    ref={(el) => { inputRefs.current[index] = el; }}
                    type="text"
                    inputMode="numeric"
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    disabled={isDisabled}
                    autoComplete="one-time-code"
                    placeholder="0"
                    error={totpError}
                    inputProps={{
                      maxLength: 1,
                    }}
                    sx={{
                      width: 60,
                      height: 60,
                      '& .MuiOutlinedInput-root': {
                        height: 60,
                        borderRadius: 2,
                        textAlign: 'center',
                        fontSize: '1.5rem',
                        fontWeight: 600,
                        backgroundColor: totpError 
                          ? alpha(theme.palette.error.main, 0.1)
                          : digit 
                            ? alpha(theme.palette.primary.main, 0.1) 
                            : 'transparent',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: totpError 
                            ? theme.palette.error.main 
                            : digit 
                              ? theme.palette.primary.main 
                              : theme.palette.divider,
                          borderWidth: totpError ? 2 : 1,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: totpError 
                            ? theme.palette.error.dark 
                            : theme.palette.primary.main,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: totpError 
                            ? theme.palette.error.main 
                            : theme.palette.primary.main,
                          borderWidth: 2,
                        },
                        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.error.main,
                        },
                      },
                      '& .MuiInputBase-input': {
                        textAlign: 'center',
                        padding: 0,
                        color: totpError ? theme.palette.error.main : 'inherit',
                      },
                    }}
                  />
                ))}
              </Box>
              
              {/* Error Text */}
              {totpError && (
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.error.main,
                    fontWeight: 500,
                    mt: 1,
                    textAlign: 'center',
                  }}
                >
                  {totpErrorMessage || 'Invalid or expired TOTP code'}
                </Typography>
              )}
            </Box>

            {/* Verify Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={!isCodeComplete || isDisabled}
              startIcon={isVerifying ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                  boxShadow: `0 6px 20px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {isVerifying ? 'Verifying...' : 'Verify Code'}
            </Button>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Resend Button */}
            <Button
              fullWidth
              variant="outlined"
              size="large"
              disabled={!canResend}
              onClick={handleResend}
              startIcon={isResending ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderColor: theme.palette.info.main,
                color: theme.palette.info.main,
                '&:hover': {
                  borderColor: theme.palette.info.dark,
                  backgroundColor: alpha(theme.palette.info.main, 0.04),
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {isResending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
            </Button>

            {/* Back to Login Button */}
            <Button
              fullWidth
              variant="text"
              size="large"
              disabled={isDisabled}
              onClick={handleCancel}
              startIcon={<ArrowBackIcon />}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.text.secondary, 0.04),
                  color: 'text.primary',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Back to Login
            </Button>
          </Box>

        </Box>
      </Fade>
    </Box>
  );
};

export default TOTPInput;
